import React from 'react';
import { Tag, Progress, Tooltip, Space } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  SyncOutlined,
  ClockCircleOutlined 
} from '@ant-design/icons';
import { GradingStatusEnum } from '../../types/grading';

interface ExamInstanceStatusProps {
  examInstanceId: string;
  gradingStatus: GradingStatusEnum;
  totalCompetencies: number;
  passedCompetencies: number;
  hasPassed: boolean | null;
  passPercentage?: number;
  showDetails?: boolean;
}

/**
 * Component to display the overall exam instance grading status.
 * Shows grading progress and pass/fail status for custom grading configurations.
 */
export const ExamInstanceStatus: React.FC<ExamInstanceStatusProps> = ({
  gradingStatus,
  totalCompetencies,
  passedCompetencies,
  hasPassed,
  passPercentage = 80,
  showDetails = true,
}) => {
  const getGradingStatusTag = () => {
    switch (gradingStatus) {
      case GradingStatusEnum.NotStarted:
        return (
          <Tag color="default" icon={<ClockCircleOutlined />}>
            Grading Not Started
          </Tag>
        );
      case GradingStatusEnum.InProgress:
        return (
          <Tag color="processing" icon={<SyncOutlined spin />}>
            Grading In Progress
          </Tag>
        );
      case GradingStatusEnum.Complete:
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Grading Complete
          </Tag>
        );
      default:
        return null;
    }
  };

  const getExamPassTag = () => {
    if (hasPassed === null) {
      return (
        <Tag color="default">
          Result Pending
        </Tag>
      );
    }
    
    if (hasPassed) {
      return (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          Exam Passed
        </Tag>
      );
    }
    
    return (
      <Tag color="error" icon={<CloseCircleOutlined />}>
        Exam Failed
      </Tag>
    );
  };

  const competencyPercent = totalCompetencies > 0 
    ? (passedCompetencies / totalCompetencies) * 100 
    : 0;
  
  const requiredCompetencies = Math.ceil(totalCompetencies * (passPercentage / 100));

  return (
    <div className="exam-instance-status">
      <Space direction="vertical" style={{ width: '100%' }}>
        <div className="status-tags">
          {getGradingStatusTag()}
          {gradingStatus === GradingStatusEnum.Complete && getExamPassTag()}
        </div>

        {showDetails && (
          <>
            <div className="competency-progress">
              <Tooltip 
                title={`${passedCompetencies} of ${totalCompetencies} competencies passed (${passPercentage}% required to pass)`}
              >
                <Progress
                  percent={competencyPercent}
                  success={{ percent: passPercentage }}
                  format={() => `${passedCompetencies}/${totalCompetencies}`}
                  strokeColor={hasPassed === true ? '#52c41a' : hasPassed === false ? '#ff4d4f' : '#1890ff'}
                />
              </Tooltip>
            </div>

            <div className="status-details">
              <span>
                Competencies: {passedCompetencies}/{totalCompetencies} passed
              </span>
              <span className="threshold">
                (need {requiredCompetencies} for {passPercentage}%)
              </span>
            </div>
          </>
        )}
      </Space>
    </div>
  );
};

export default ExamInstanceStatus;
